import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Contact.css';

const API_BASE =
  (process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== '')
    ? process.env.REACT_APP_API_URL.replace(/\/$/, '')
    : process.env.NODE_ENV === 'development'
      ? 'http://localhost:8000'
      : window.location.origin;

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

function loadRecaptchaScript(siteKey) {
  if (!siteKey || typeof window === 'undefined') return Promise.resolve();
  if (window.grecaptcha && window.grecaptcha.execute) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
    document.head.appendChild(script);
  });
}

function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [recaptchaReady, setRecaptchaReady] = useState(!RECAPTCHA_SITE_KEY);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    loadRecaptchaScript(RECAPTCHA_SITE_KEY)
      .then(() => setRecaptchaReady(true))
      .catch(() => setRecaptchaReady(false));
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const getRecaptchaToken = () => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha || !window.grecaptcha.execute) return Promise.resolve(null);
    return window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      // reCAPTCHA token is required by the API
      if (!RECAPTCHA_SITE_KEY || !recaptchaReady) {
        setStatus(null);
        setError(t('contact.form.recaptchaError'));
        return;
      }
      let recaptchaToken;
      try {
        recaptchaToken = await getRecaptchaToken();
      } catch (err) {
        setStatus(null);
        setError(t('contact.form.recaptchaError'));
        return;
      }
      if (!recaptchaToken) {
        setStatus(null);
        setError(t('contact.form.recaptchaError'));
        return;
      }
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        recaptcha_token: recaptchaToken
      };

      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus(null);
        const msg = res.status === 404
          ? t('contact.form.error404')
          : (data.detail || t('contact.form.error'));
        setError(msg);
        return;
      }
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus(null), 5000);
    } catch (err) {
      setStatus(null);
      setError(t('contact.form.error'));
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <div className="contact-header">
          <h1>{t('contact.title')}</h1>
          <p>{t('contact.subtitle')}</p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <div className="info-card">
              <div className="info-icon">📧</div>
              <h3>{t('contact.email.title')}</h3>
              <p>contact@dailyzodiac.ai</p>
            </div>

            <div className="info-card">
              <div className="info-icon">🌐</div>
              <h3>{t('contact.website.title')}</h3>
              <p>dailyzodiac.ai</p>
            </div>

            <div className="info-card">
              <div className="info-icon">⏰</div>
              <h3>{t('contact.response.title')}</h3>
              <p>{t('contact.response.text')}</p>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">{t('contact.form.name')}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('contact.form.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">{t('contact.form.subject')}</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">{t('contact.form.message')}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="6"
                required
              />
            </div>

            {status === 'success' && (
              <div className="form-success">
                {t('contact.form.success')}
              </div>
            )}

            {error && (
              <div className="form-error" role="alert">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={status === 'sending' || (RECAPTCHA_SITE_KEY && !recaptchaReady)}
            >
              {status === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
            </button>
            {RECAPTCHA_SITE_KEY && (
              <p className="contact-recaptcha-notice">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                {' '}and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default Contact;
